import React, { useState, useEffect } from "react";
import {
  Edit3,
  X,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  Shield,
  CheckCircle,
} from "lucide-react";
import styles from "./AccountDetailsPage.module.css";
import { getAccessToken } from "../../../utils/authStorage";

export const AccountDetailsPage = ({ onBack, isMobile = false }) => {
  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    mobile: "",
    gender: "",
    state: "",
    role: "user",
    avatar: "",
    twoFactorEnabled: false,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [show2faSetup, setShow2faSetup] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [is2faLoading, setIs2faLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: "",
  });

  const API_URL =
    import.meta.env.VITE_API_URL || "https://pixelmoonstore.in/api";

  const fetchUserDetails = async () => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUserDetails({
          name: data.user.name || "",
          email: data.user.email || "",
          mobile: data.user.mobile || "",
          gender: data.user.gender || "",
          state: data.user.state || "",
          role: data.user.role || "user",
          avatar: data.user.avatar || "",
          twoFactorEnabled: data.user.twoFactorEnabled || false,
        });
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const handleUpdateProfile = async () => {
    setUpdating(true);
    try {
      // Validate passwords if changing
      if (passwords.new && passwords.new !== passwords.confirm) {
        throw new Error("New passwords do not match");
      }

      const token = getAccessToken();
      const response = await fetch(`${API_URL}/auth/update-profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: userDetails.name,
          gender: userDetails.gender,
          state: userDetails.state,
          ...(passwords.new && {
            currentPassword: passwords.current,
            newPassword: passwords.new,
          }),
        }),
      });

      if (response.ok) {
        alert("Profile updated successfully!");
        setIsEditing(false);
        setPasswords({ current: "", new: "", confirm: "" });
        // If mobile and has onBack callback, go back after successful update
        if (isMobile && onBack) {
          setTimeout(() => onBack(), 1500);
        }
      } else {
        const error = await response.json();
        throw new Error(error.message);
      }
    } catch (error) {
      alert(error.message || "Failed to update profile");
    } finally {
      setUpdating(false);
    }
  };

  const handleInitiate2FA = async () => {
    setIs2faLoading(true);
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_URL}/auth/2fa/setup`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setQrCode(data.qrCode);
        setShow2faSetup(true);
      } else {
        alert(data.message || "Failed to initiate 2FA setup");
      }
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setIs2faLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (verificationCode.length !== 6) {
      alert("Please enter a 6-digit code");
      return;
    }
    setIs2faLoading(true);
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_URL}/auth/2fa/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ token: verificationCode }),
      });
      const data = await response.json();
      if (data.success) {
        alert("2FA enabled successfully!");
        setShow2faSetup(false);
        setQrCode("");
        setVerificationCode("");
        fetchUserDetails();
      } else {
        alert(data.message || "Verification failed");
      }
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setIs2faLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    const code = prompt(
      "Enter 6-digit code from your authenticator app to disable 2FA:",
    );
    if (!code) return;

    setIs2faLoading(true);
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_URL}/auth/2fa/disable`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ token: code }),
      });
      const data = await response.json();
      if (data.success) {
        alert("2FA disabled successfully");
        fetchUserDetails();
      } else {
        alert(data.message || "Failed to disable 2FA");
      }
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setIs2faLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleCancel = () => {
    setIsEditing(false);
    setPasswords({ current: "", new: "", confirm: "" });
    // Reset any unsaved changes
    fetchUserDetails();
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <RefreshCw size={32} className={styles.spin} />
        <p>Loading account details...</p>
      </div>
    );
  }

  return (
    <div
      className={`${styles.accountPage} ${isMobile ? styles.mobileView : ""}`}
    >
      {!isMobile && (
        <div className={styles.pageHeader}>
          <h2>Account Details</h2>
          <p>Manage your personal information and security settings</p>
        </div>
      )}

      <div className={styles.accountContainer}>
        <div className={styles.profileHeader}>
          <div className={styles.avatar} style={{ overflow: "hidden" }}>
            {userDetails.avatar ? (
              <img
                src={userDetails.avatar}
                alt={userDetails.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              userDetails.name.charAt(0).toUpperCase()
            )}
          </div>
          <div className={styles.profileInfo}>
            <h3>{userDetails.name}</h3>
            <span className={`${styles.roleBadge} ${styles[userDetails.role]}`}>
              {userDetails.role.toUpperCase()}
            </span>
          </div>
          <div className={styles.profileActions}>
            {isMobile && onBack && (
              <button
                className={styles.btnOutline}
                onClick={onBack}
                style={{ marginRight: "8px" }}
              >
                Back
              </button>
            )}
            <button
              className={styles.btnOutline}
              onClick={() => (isEditing ? handleCancel() : setIsEditing(true))}
            >
              {isEditing ? <X size={16} /> : <Edit3 size={16} />}
              {isEditing ? "Cancel" : "Edit Profile"}
            </button>
          </div>
        </div>

        <div className={styles.profileForm}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Full Name</label>
              <input
                type="text"
                className={styles.formControl}
                value={userDetails.name}
                onChange={(e) =>
                  setUserDetails({ ...userDetails, name: e.target.value })
                }
                disabled={!isEditing}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Email Address</label>
              <input
                type="email"
                className={styles.formControl}
                value={userDetails.email}
                disabled
              />
              <small className={styles.formText}>Email cannot be changed</small>
            </div>

            <div className={styles.formGroup}>
              <label>Mobile Number</label>
              <input
                type="text"
                className={styles.formControl}
                value={userDetails.mobile}
                disabled
              />
              <small className={styles.formText}>
                Mobile number cannot be changed
              </small>
            </div>

            <div className={styles.formGroup}>
              <label>Gender</label>
              <select
                className={styles.formControl}
                value={userDetails.gender}
                onChange={(e) =>
                  setUserDetails({ ...userDetails, gender: e.target.value })
                }
                disabled={!isEditing}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className={styles.formGroupFull}>
              <label>State</label>
              <input
                type="text"
                className={styles.formControl}
                placeholder="Enter your state"
                value={userDetails.state}
                onChange={(e) =>
                  setUserDetails({ ...userDetails, state: e.target.value })
                }
                disabled={!isEditing}
              />
            </div>
          </div>

          {isEditing && (
            <div className={styles.passwordSection}>
              <h4>Change Password (Optional)</h4>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Current Password</label>
                  <div className={styles.passwordInput}>
                    <input
                      type={showPassword.current ? "text" : "password"}
                      className={styles.formControl}
                      placeholder="Enter current password"
                      value={passwords.current}
                      onChange={(e) =>
                        setPasswords({ ...passwords, current: e.target.value })
                      }
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => togglePasswordVisibility("current")}
                    >
                      {showPassword.current ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>New Password</label>
                  <div className={styles.passwordInput}>
                    <input
                      type={showPassword.new ? "text" : "password"}
                      className={styles.formControl}
                      placeholder="Enter new password"
                      value={passwords.new}
                      onChange={(e) =>
                        setPasswords({ ...passwords, new: e.target.value })
                      }
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => togglePasswordVisibility("new")}
                    >
                      {showPassword.new ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>Confirm Password</label>
                  <div className={styles.passwordInput}>
                    <input
                      type={showPassword.confirm ? "text" : "password"}
                      className={styles.formControl}
                      placeholder="Confirm new password"
                      value={passwords.confirm}
                      onChange={(e) =>
                        setPasswords({ ...passwords, confirm: e.target.value })
                      }
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => togglePasswordVisibility("confirm")}
                    >
                      {showPassword.confirm ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                  {passwords.new &&
                    passwords.confirm &&
                    passwords.new !== passwords.confirm && (
                      <small className={styles.errorText}>
                        Passwords do not match
                      </small>
                    )}
                </div>
              </div>
            </div>
          )}

          {isEditing && (
            <div className={styles.formActions}>
              <button
                className={styles.btnPrimary}
                onClick={handleUpdateProfile}
                disabled={
                  updating ||
                  (passwords.new && passwords.new !== passwords.confirm)
                }
              >
                {updating ? (
                  <>
                    <RefreshCw size={16} className={styles.spin} />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Two-Factor Authentication Section */}
        <div
          className={styles.passwordSection}
          style={{
            borderTop: "1px solid var(--border-color)",
            padding: "2rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "1rem",
            }}
          >
            <Shield size={20} color="var(--primary-color)" />
            <h4 style={{ margin: 0, color: "var(--text-color)" }}>
              Two-Factor Authentication (TOTP)
            </h4>
          </div>
          <p
            style={{
              color: "var(--text-color)",
              opacity: 0.7,
              marginBottom: "1.5rem",
              fontSize: "0.9rem",
            }}
          >
            Add an extra layer of security to your account by requiring a code
            from your authenticator app.
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "var(--background-color)",
              padding: "1rem",
              borderRadius: "8px",
              border: "1px solid var(--border-color)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <span style={{ color: "var(--text-color)", fontWeight: 500 }}>
                Status:
              </span>
              <span
                style={{
                  padding: "0.25rem 0.75rem",
                  borderRadius: "20px",
                  fontSize: "0.8rem",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                  background: userDetails.twoFactorEnabled
                    ? "#10b981"
                    : "#6b7280",
                  color: "white",
                }}
              >
                {userDetails.twoFactorEnabled ? (
                  <>
                    <CheckCircle size={14} /> Enabled
                  </>
                ) : (
                  "Disabled"
                )}
              </span>
            </div>

            {!userDetails.twoFactorEnabled ? (
              <button
                className={styles.btnPrimary}
                onClick={handleInitiate2FA}
                disabled={is2faLoading}
              >
                {is2faLoading ? (
                  <RefreshCw size={16} className={styles.spin} />
                ) : (
                  "Enable 2FA"
                )}
              </button>
            ) : (
              <button
                className={styles.btnPrimary}
                style={{ background: "#dc3545" }}
                onClick={handleDisable2FA}
                disabled={is2faLoading}
              >
                Disable 2FA
              </button>
            )}
          </div>

          {show2faSetup && (
            <div
              style={{
                marginTop: "1.5rem",
                padding: "1.5rem",
                border: "1px solid var(--border-color)",
                borderRadius: "8px",
                background: "var(--hover-color)",
              }}
            >
              <h5 style={{ color: "var(--text-color)", marginBottom: "1rem" }}>
                Setup Authenticator App
              </h5>
              <ol
                style={{
                  color: "var(--text-color)",
                  paddingLeft: "1.2rem",
                  fontSize: "0.9rem",
                  lineHeight: "1.6",
                }}
              >
                <li>
                  Scan this QR code with your authenticator app (Google
                  Authenticator, Authy, etc.):
                </li>
                <div
                  style={{
                    background: "white",
                    padding: "1rem",
                    width: "fit-content",
                    margin: "1rem 0",
                    borderRadius: "8px",
                  }}
                >
                  {qrCode && (
                    <img
                      src={qrCode}
                      alt="2FA QR Code"
                      style={{ width: "150px", height: "150px" }}
                    />
                  )}
                </div>
                <li>Enter the 6-digit code generated by the app:</li>
              </ol>
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  marginTop: "1rem",
                  alignItems: "center",
                }}
              >
                <input
                  type="text"
                  maxLength="6"
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) =>
                    setVerificationCode(e.target.value.replace(/\D/g, ""))
                  }
                  className={styles.formControl}
                  style={{
                    width: "120px",
                    textAlign: "center",
                    fontSize: "1.2rem",
                    letterSpacing: "2px",
                  }}
                />
                <button
                  className={styles.btnPrimary}
                  onClick={handleVerify2FA}
                  disabled={is2faLoading || verificationCode.length !== 6}
                >
                  {is2faLoading ? "Verifying..." : "Verify & Enable"}
                </button>
                <button
                  className={styles.btnOutline}
                  onClick={() => setShow2faSetup(false)}
                  style={{
                    color: "var(--text-color)",
                    borderColor: "var(--border-color)",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
